"use client";

import type { ConversationData } from "@/features/chat/Types/conversations";

import { AnimatePresence } from "motion/react";
import { useState } from "react";
import { ChevronDown, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PopupChat } from "./PopupChat";

import { useChatStore } from "@/features/chat/store/useChatStore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile.tsx";

export function PopupChatWrapper() {
  const { openConversations, minimizedConversations, toggleMinimized, closeConversation } =
    useChatStore();
  const isMobile = useIsMobile();
  const [openConversation, setOpenConversation] = useState<ConversationData | undefined>(undefined);
  const { t } = useTranslation(["chat"]);

  if (!openConversations) return null;

  if (isMobile && openConversations.length > 1) {
    if (openConversation) {
      return (
        <div className="fixed right-0 bottom-0 z-50 flex items-end space-x-2">
          <AnimatePresence>
            <PopupChat
              key={openConversation.id}
              conversation={openConversation}
              isMinimized={false}
              onClose={() => closeConversation(openConversation.id)}
              onMinimize={() => setOpenConversation(undefined)}
            />
          </AnimatePresence>
        </div>
      );
    }

    return (
      <div className="fixed right-4 bottom-4 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="bg-side-accent hover:bg-muted flex items-center gap-2 rounded-full px-4 shadow-lg transition-all"
              size="lg"
            >
              <MessageCircle className="h-5 w-5" />
              <span className="font-medium">{t("chats")}</span>
              <div className="bg-primary-foreground text-muted-foreground flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold">
                {openConversations.length}
              </div>
              <ChevronDown className="ml-1 h-4 w-4 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="border-border/50 bg-side-accent/80 animate-in fade-in-80 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 w-64 rounded-xl p-2 shadow-lg"
          >
            <div className="text-muted-foreground px-2 py-1.5 text-sm font-medium">
              {t("selectConversation")}
            </div>
            {openConversations.map((conversation) => (
              <DropdownMenuItem
                key={conversation.id}
                className={cn(
                  "my-1 flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors",
                  "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                )}
                onClick={() => setOpenConversation(conversation)}
              >
                <div className="relative flex-shrink-0">
                  <img
                    alt={conversation.name}
                    className="border-border/50 h-8 w-8 rounded-full border object-cover"
                    src={conversation.avatar || "/placeholder.svg"}
                  />
                  <div className="border-background absolute right-0 bottom-0 h-2.5 w-2.5 rounded-full border-2 bg-green-500" />
                </div>
                <div className="flex flex-col">
                  <span className="leading-none font-medium">{conversation.name}</span>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="fixed right-0 bottom-0 z-50 flex items-end space-x-2 sm:right-4">
      <AnimatePresence>
        {openConversations.map((conversation) => (
          <PopupChat
            key={conversation.id}
            conversation={conversation}
            isMinimized={minimizedConversations.includes(conversation.id)}
            onClose={() => closeConversation(conversation.id)}
            onMinimize={() => toggleMinimized(conversation.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
